import { useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, Divider, Paper, Stack, Typography,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { creativeGenerationService } from '../../services/creative-generation.service';

const STATUS_COLOUR: Record<string, 'default' | 'primary' | 'success' | 'error' | 'warning'> = {
  proposed: 'default', approved: 'primary', rejected: 'error',
  queued: 'warning', generating: 'warning', succeeded: 'success', failed: 'error',
};

export default function CreativeGenerationPage() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [lightbox, setLightbox] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['creative-run', runId],
    queryFn: () => creativeGenerationService.getRun(runId!),
    enabled: !!runId,
    refetchInterval: (q) => {
      const rows = q.state.data?.prompts ?? [];
      return rows.some((p) => p.status === 'queued' || p.status === 'generating') ? 3000 : false;
    },
  });

  const prompts = data?.prompts ?? [];
  const proposed = prompts.filter((p) => p.status === 'proposed');
  const approved = prompts.filter((p) => p.status === 'approved');

  const decide = useMutation({
    mutationFn: (status: 'approved' | 'rejected') =>
      creativeGenerationService.decide(runId!, [...picked], status),
    onSuccess: (changed, status) => {
      enqueueSnackbar(`${changed} ${status}`, { variant: 'success' });
      setPicked(new Set());
      queryClient.invalidateQueries({ queryKey: ['creative-run', runId] });
    },
    onError: () => enqueueSnackbar('Could not record that decision', { variant: 'error' }),
  });

  const generate = useMutation({
    mutationFn: () => creativeGenerationService.generate(runId!),
    onSuccess: (queued) => {
      enqueueSnackbar(`${queued} queued for generation`, { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['creative-run', runId] });
    },
    onError: () => enqueueSnackbar('Could not start generation', { variant: 'error' }),
  });

  const toggle = (id: string) => setPicked((prev) => {
    const next = new Set(prev);
    if (!next.delete(id)) next.add(id);
    return next;
  });

  if (isLoading) return <Box sx={{ p: 4 }}><CircularProgress /></Box>;
  if (!data) return <Box sx={{ p: 4 }}><Alert severity="error">Run not found</Alert></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 1100 }} data-testid="creative-run">
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="h5" sx={{ flex: 1 }}>Review variations</Typography>
        <Chip size="small" label={data.run.status} />
        <Button size="small" onClick={() => navigate('/ads-manager')}>Back to Ads Manager</Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {prompts.length} proposed from {data.run.source_creative_ids.length} creative
        {data.run.source_creative_ids.length === 1 ? '' : 's'}. Nothing is generated until you approve it.
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button size="small" variant="contained" disabled={picked.size === 0 || decide.isPending}
          onClick={() => decide.mutate('approved')} data-testid="approve">
          Approve {picked.size || ''}
        </Button>
        <Button size="small" variant="outlined" color="error" disabled={picked.size === 0 || decide.isPending}
          onClick={() => decide.mutate('rejected')} data-testid="reject">
          Reject {picked.size || ''}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" color="secondary" disabled={approved.length === 0 || generate.isPending}
          onClick={() => generate.mutate()} data-testid="generate">
          Generate {approved.length || ''} approved
        </Button>
      </Stack>

      {proposed.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>Every proposal has been decided.</Alert>
      )}

      <Paper variant="outlined">
        {prompts.map((p, i) => (
          <Box key={p.id} data-testid="prompt-row">
            {i > 0 && <Divider />}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1.5 }}>
              <Checkbox
                size="small"
                disabled={p.status !== 'proposed'}
                checked={picked.has(p.id)}
                onChange={() => toggle(p.id)}
              />
              <Thumb url={p.source_url} label="source" onOpen={setLightbox} />
              <Typography variant="body2" sx={{ flex: 1 }}>{p.prompt}</Typography>
              {p.error_message && (
                <Typography variant="caption" color="error" sx={{ maxWidth: 260 }}>{p.error_message}</Typography>
              )}
              <Thumb url={p.result_url} label="result" onOpen={setLightbox} />
              <Chip size="small" label={p.status} color={STATUS_COLOUR[p.status] ?? 'default'} />
            </Stack>
          </Box>
        ))}
      </Paper>

      <Dialog open={!!lightbox} onClose={() => setLightbox(null)} maxWidth="lg">
        <Box
          component="img"
          src={lightbox ?? ''}
          alt=""
          sx={{ display: 'block', maxWidth: '90vw', maxHeight: '85vh' }}
        />
      </Dialog>
    </Box>
  );
}

function Thumb({ url, label, onOpen }: {
  url: string | null; label: string; onOpen: (u: string) => void;
}) {
  if (!url) return <Box sx={{ width: 48, height: 48, bgcolor: 'action.hover', borderRadius: 1 }} />;
  return (
    <Box
      component="img"
      src={url}
      alt={label}
      onClick={() => onOpen(url)}
      sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1, cursor: 'zoom-in' }}
    />
  );
}
